import React, { useEffect, useRef, useState } from 'react';
import { Alignment } from '@philips-internal/oswald-ui-constants';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { LanguageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { useSharedState } from '@philips-internal/oswald-ui-engine';
import { classNames, isContentfulString, isEnumValid, isFunction, loadScript } from '@philips-internal/oswald-ui-utils';
import { RecaptchaVersion } from './recaptcha-constants.js';
import { RecaptchaProperties } from './recaptcha-types.js';
import { executeGrecaptcha } from './recaptcha-utils.js';
import './recaptcha.css';
const scriptTagIdentifier = 'oswald-recaptcha';
function getRecaptchaScriptTag() {
    return document.querySelector(`script[identifier="${scriptTagIdentifier}"]`);
}
function hasRecaptcha() {
    return Boolean(document.querySelector('.c-recaptcha'));
}
function removeRecaptchaScriptTag() {
    if (hasRecaptcha()) {
        return;
    }
    getRecaptchaScriptTag()?.remove();
}
function Recaptcha(props) {
    const { alignment = RecaptchaProperties.alignment.defaultValue, enabled = RecaptchaProperties.enabled.defaultValue, onChange, siteKey = RecaptchaProperties.siteKey.defaultValue, size = RecaptchaProperties.size.defaultValue, tabIndex = RecaptchaProperties.tabIndex.defaultValue, theme = RecaptchaProperties.theme.defaultValue, type = RecaptchaProperties.type.defaultValue, version = RecaptchaProperties.version.defaultValue, value, } = props;
    const captchaRef = useRef(null);
    const [recaptchaUrl, setRecaptchaUrl] = useState('');
    const [response, setResponse] = useSharedState({
        initialValue: '',
        name: 'response',
        props,
    });
    const updateResponse = (newResponse) => {
        setResponse(newResponse);
        if (!isFunction(onChange)) {
            return;
        }
        onChange(new ChangeEvent({ oldValue: response, value: newResponse }));
    };
    const onExpireCallback = () => {
        if (!window.grecaptcha) {
            return;
        }
        window.grecaptcha.reset();
        updateResponse('');
    };
    const renderV2Grecaptcha = () => {
        if (!window.grecaptcha) {
            return;
        }
        if (version !== RecaptchaVersion.V2) {
            return;
        }
        if (!captchaRef || !captchaRef.current) {
            return;
        }
        window.grecaptcha.render(captchaRef.current, {
            'callback': updateResponse,
            'expired-callback': onExpireCallback,
            'sitekey': siteKey,
            'size': size,
            'tabindex': tabIndex,
            'theme': theme,
            'type': type,
        });
    };
    const initializeGrecaptcha = () => {
        if (!window.grecaptcha) {
            return;
        }
        void window.grecaptcha.ready(() => {
            renderV2Grecaptcha();
            executeGrecaptcha({
                onExpireCallback: onExpireCallback,
                onLoadCallback: updateResponse,
                siteKey,
                version,
            });
        });
    };
    useEffect(() => {
        if (value !== response) {
            updateResponse(value ?? '');
        }
    }, [value]);
    useEffect(() => {
        if (!isContentfulString(siteKey)) {
            return;
        }
        const hl = LanguageService.getCurrentLanguage();
        const baseURL = `https://www.google.com/recaptcha/api.js?hl=${hl}`;
        const renderQueryParam = `&render=${version === RecaptchaVersion.V3 ? siteKey : 'explicit'}`;
        setRecaptchaUrl(`${baseURL}${renderQueryParam}`);
    }, [siteKey, version]);
    useEffect(() => {
        if (!isContentfulString(recaptchaUrl)) {
            return;
        }
        if (getRecaptchaScriptTag()) {
            // Apparently if more than one grecaptcha is executed at the same time one of them fail. This timeout prevents this from happening
            const timeoutId = window.setTimeout(() => {
                initializeGrecaptcha();
            }, 200);
            return () => {
                window.clearTimeout(timeoutId);
                removeRecaptchaScriptTag();
            };
        }
        void loadScript({
            async: true,
            defer: true,
            identifier: scriptTagIdentifier,
            src: recaptchaUrl,
        })
            .then(initializeGrecaptcha)
            .catch((error) => {
            let message = error?.message ?? '';
            if (!isContentfulString(message)) {
                return;
            }
            if (message.includes('Failed to load script')) {
                /**
                 * As the original error from recaptcha library load fail do not return useful details about what happened,
                 * this explanation is here to help with the most probable reason
                 */
                message = '[Recaptcha]: failed to load recaptcha library script. Make sure your site key is valid for the recaptcha version being used';
            }
            throw new Error(message);
        });
        return () => removeRecaptchaScriptTag();
    }, [recaptchaUrl]);
    /**
     * Recaptcha v3 badge is injected by the library itself
     */
    if (version === RecaptchaVersion.V3) {
        return null;
    }
    const className = classNames({
        'c-recaptcha': true,
        'c-recaptcha--disabled': !enabled,
        [`c-recaptcha--${alignment}`]: isEnumValid(Alignment, alignment),
    });
    return (React.createElement("div", { className: className, ref: captchaRef }));
}
export { Recaptcha, };
//# sourceMappingURL=recaptcha.js.map